import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HoverHint } from '@/features/engagement/components/EngagementNotReady';
import { formatCompact } from '@/lib/money';
import { cn } from '@/lib/utils';
import type { DashboardBreakdownItem } from '../api';

const BAR_COLORS = ['#2a78d6', '#1baf7a', '#eda100', '#9b45ef', '#20aee8', '#f43f4f', '#898781'];

/**
 * Confluence §5 Sign-ups by Location — ranked bars for new sign-ups
 * (country or region/state).
 */
export function DashboardSignupsByLocationCard({
  title = 'Signups by Location',
  items,
  total,
  isLoading,
  note,
  hint,
  emptyMessage = 'No sign-ups in this period.',
}: {
  title?: string;
  items: DashboardBreakdownItem[];
  total?: number;
  isLoading?: boolean;
  note?: string;
  /** Data-source explanation shown on title hover. */
  hint?: string;
  emptyMessage?: string;
}) {
  const rows = items.slice(0, 8);
  const titleNode = <CardTitle className="text-sm font-medium text-foreground">{title}</CardTitle>;

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        {hint ? (
          <HoverHint
            text={hint}
            label={`Where ${title} comes from. ${hint}`}
            className="cursor-help"
          >
            {titleNode}
          </HoverHint>
        ) : (
          titleNode
        )}
        <p className="text-[11px] text-muted-foreground">
          {note ??
            'Completed signups by signup country (IP at registration). Missing snapshot usually appears as Unknown.'}
          {total != null ? ` · ${formatCompact(total)} total` : ''}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-1">
        {isLoading ? (
          <p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">{emptyMessage}</p>
        ) : (
          rows.map((item, index) => {
            const pct = Math.max(0, Math.min(100, Number(item.percent ?? 0)));
            const color = BAR_COLORS[index % BAR_COLORS.length];
            const delta = item.deltaPercent;
            const hasDelta = delta != null && Number.isFinite(delta);
            return (
              <div key={item.key ?? item.label} className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="truncate font-medium text-foreground">{item.label ?? item.key}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatCompact(item.count ?? 0)}
                    {hasDelta ? (
                      <span
                        className={cn(
                          'ml-2',
                          (delta ?? 0) >= 0 ? 'text-sky-400' : 'text-rose-400',
                        )}
                      >
                        {(delta ?? 0) >= 0 ? '+' : ''}
                        {Number(delta).toFixed(1)}%
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted/50">
                    <div
                      className="h-full rounded-full transition-[width]"
                      style={{ width: `${pct}%`, backgroundColor: color }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
                    {pct.toFixed(0)}%
                  </span>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
